import { cloneElement, CSSProperties, ReactElement, useRef, useState } from 'react'

import { Option } from 'components/elements/FloatingToolbar/types'

import { useEnableClassNameOnScrollDown, useHideOnScroll, useStopBodyScroll } from 'hooks/useScrollEffects'
import { useSwipeEffects } from 'hooks/useSwipeEffects'

import FloatingToolbarGridOption from 'components/elements/FloatingToolbar/GridItem'
import FloatingToolbarListOption from 'components/elements/FloatingToolbar/ListItem'
import { useTransition } from 'hooks/useTransitions'

import styles from './floatingToolbar.module.scss'

const MIN_HEIGHT = 70
const TOTAL_MAX_HEIGHT_PERCENT = 0.75

type Props = {
  options: Option[]
  gridColumns?: number

  maxHeightOverride?: number

  includeShrinkOption?: boolean
  noHideOnScroll?: boolean

  hideOnScrollRef?: HTMLDivElement | null // Assumed to be the document if not present

  style?: CSSProperties
  className?: string
  toolbarClassName?: string
  subComponentClassName?: string
}

const FloatingToolbar = ({
  options,
  gridColumns = 4,

  maxHeightOverride,

  includeShrinkOption,
  noHideOnScroll,

  hideOnScrollRef,

  style = {},
  className = '',
  toolbarClassName = '',
  subComponentClassName = '',
}: Props) => {
  const startingHeight = useRef(0)

  const openBodyScrollRef = useRef<HTMLDivElement | null>(null)

  const [open, setOpen] = useState(false)
  const [height, setHeight] = useState(MIN_HEIGHT)
  const [shrunk, setShrunk] = useState(false)

  const [flingMenuOptions, setFlingMenuOptions] = useState<Option[] | null>(null)
  const [maxHeight, setMaxHeight] = useState<number>(getMaxHeight(maxHeightOverride))
  const [subComponent, setSubComponent] = useState<ReactElement | null>(null)

  const scrollingDown = noHideOnScroll ? false : useHideOnScroll(3, hideOnScrollRef)
  const setNoScroll = useStopBodyScroll(open)

  const [subContentTransition] = useTransition(open, { displayNoneOnHidden: true })
  const [primaryContentTransition] = useTransition(!open, {
    displayNoneOnHidden: true,
    inDuration: 200,
    outDuration: 100,
  })

  const swipeRef = useSwipeEffects('vertical', {
    swippingClassName: styles.activelyDragging,
    checkCanSwipe: () => open && openBodyScrollRef.current?.scrollTop === 0,
    onTouchStart: () => (startingHeight.current = height),
    onTouchMove: offset => handleSetHeight(startingHeight.current + offset),
    onTouchEnd: (offset, _, speed) => {
      // offset > 0 means we've swipped upward
      if (offset > 0 || height === maxHeight) return

      const swipeThresholdMet = Math.abs(offset) > maxHeight / 2 || speed > 0.8

      handleSetOpen(!swipeThresholdMet, maxHeight, flingMenuOptions, subComponent)
    },
  })

  useEnableClassNameOnScrollDown('floating-toolbar', scrollingDown)

  const handleSetHeight = (desiredHeight: number) => {
    if (desiredHeight > getMaxHeight(desiredHeight)) return setHeight(maxHeight)
    if (desiredHeight < MIN_HEIGHT) return setHeight(MIN_HEIGHT)

    setHeight(desiredHeight)
  }

  const handleSetOpen = (
    drawerIsOpen: boolean,
    overideHeight: number = maxHeight,
    options: Option[] | null = null,
    subComponent: ReactElement | null = null,
  ) => {
    if (drawerIsOpen) {
      setMaxHeight(overideHeight)
      handleSetHeight(overideHeight)
      setSubComponent(subComponent)
      setFlingMenuOptions(options)
      setOpen(true)
      setNoScroll(true)

      return
    }

    setMaxHeight(getMaxHeight(maxHeightOverride))
    handleSetHeight(MIN_HEIGHT)
    setSubComponent(null)
    setFlingMenuOptions(null)
    setOpen(false)
    setTimeout(() => setNoScroll(false), 100) // Prevent overscrolling when swipping "away" the drawer from scrolling the content below

    openBodyScrollRef.current?.scrollTo({ top: 0 })
  }

  const handleSetHidden = (hidden: boolean) => {
    setShrunk(hidden)

    if (hidden) setOpen(false)
  }

  const handleOptionClick = (option: Option) => {
    if (option.onClick) return option.onClick()
    if (option.subOptions) return handleSetOpen(true, option.maxHeightOverride, option.subOptions)
    if (option.subComponent) return handleSetOpen(true, option.maxHeightOverride, null, option.subComponent)
  }

  const openPercent = height / maxHeight

  return (
    <>
      <div
        className={styles.wrapper}
        onClick={() => handleSetOpen(false, maxHeight, null, null)}
        style={{
          pointerEvents: open ? 'all' : 'none',
          backgroundColor:
            height !== MIN_HEIGHT ? ` rgb(from var(--background-color) r g b / ${openPercent - 0.2})` : 'unset',
        }}
      />

      <div
        ref={swipeRef}
        style={{ ...style, height, touchAction: !open ? 'none' : 'unset' }}
        className={`${styles.container} ${shrunk ? styles.shrunk : ''} ${scrollingDown ? styles.hidden : ''} ${className}`}>
        <div className={`${styles.toolbar} ${open ? styles.open : ''} ${toolbarClassName}`}>
          <button className={styles.dragHandle} onClick={() => open && handleSetOpen(false, maxHeight, null, null)}>
            {open && <span />}
          </button>

          <div
            ref={primaryContentTransition}
            className={styles.options}
            style={{ gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))` }}>
            {!shrunk &&
              options.map((option, index) => {
                if (option.type === 'blank') return <div key={index} />

                return <FloatingToolbarGridOption key={index} {...option} onClick={() => handleOptionClick(option)} />
              })}

            {includeShrinkOption && (
              <FloatingToolbarGridOption
                icon={shrunk ? 'arrow circle left' : 'arrow circle right'}
                label={shrunk ? 'Toolbar' : 'Hide'}
                onClick={() => handleSetHidden(!shrunk)}
              />
            )}
          </div>

          <div
            className={styles.openMenuContent}
            ref={element => {
              subContentTransition.current = element
              openBodyScrollRef.current = element
            }}>
            <div
              className={subComponentClassName}
              style={{ opacity: !open || openPercent === 1 ? 1 : openPercent - 0.2 }}>
              {flingMenuOptions && (
                <div className={styles.list}>
                  {flingMenuOptions.map((option, index) => (
                    <FloatingToolbarListOption
                      key={index}
                      {...option}
                      onClick={() => {
                        if (option.onClick) option.onClick()

                        handleSetOpen(false)
                      }}
                    />
                  ))}
                </div>
              )}

              {subComponent &&
                cloneElement(subComponent, { onClose: () => handleSetOpen(false, maxHeight, null, null) })}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default FloatingToolbar

export const InternalFloatingNavbar = (props: Props) => (
  <FloatingToolbar noHideOnScroll className={styles.internal} {...props} />
)

const getMaxHeight = (maxHeightOverride?: number) => {
  if (typeof window === 'undefined') return MIN_HEIGHT

  const absoluteMax = window?.innerHeight * TOTAL_MAX_HEIGHT_PERCENT // 80% of the height of the page

  if (maxHeightOverride === undefined) return absoluteMax
  if (absoluteMax < maxHeightOverride) return absoluteMax

  return maxHeightOverride
}
